import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { buyerAuthService } from '../../services/buyerService';
import { FullWidthSection, ContentWrapper } from '../../components/ui/Layout';
import { useLanguage } from '../../contexts/LanguageContext';

const BuyerRegistration = () => {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    country: 'China',
    businessLicenseNumber: '',
    importLicenseNumber: '',
    preferredLanguage: 'zh',
    accountType: 'individual'
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const content = {
    en: {
      title: 'Buyer Registration',
      subtitle: 'Create your account to access personalized features',
      accountType: 'Account Type',
      individual: 'Individual Buyer',
      company: 'Company Representative',
      fullName: 'Full Name',
      email: 'Email Address',
      phone: 'Phone Number',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      companyName: 'Company Name',
      country: 'Country',
      businessLicense: 'Business License Number',
      importLicense: 'Import License Number',
      preferredLanguage: 'Preferred Language',
      english: 'English',
      chinese: 'Chinese (Simplified)',
      submit: 'Create Account',
      submitting: 'Creating Account...',
      successTitle: 'Registration Successful!',
      successMessage: 'Please check your email to verify your account before logging in.',
      goToLogin: 'Go to Login',
      alreadyHaveAccount: 'Already have an account?',
      signIn: 'Sign In',
      passwordStrength: 'Password Strength:',
      weak: 'Weak',
      medium: 'Medium',
      strong: 'Strong',
      veryStrong: 'Very Strong',
      optional: 'Optional'
    },
    zh: {
      title: '买家注册',
      subtitle: '创建您的账户以访问个性化功能',
      accountType: '账户类型',
      individual: '个人买家',
      company: '公司代表',
      fullName: '姓名',
      email: '电子邮件地址',
      phone: '电话号码',
      password: '密码',
      confirmPassword: '确认密码',
      companyName: '公司名称',
      country: '国家',
      businessLicense: '营业执照号码',
      importLicense: '进口许可证号码',
      preferredLanguage: '首选语言',
      english: '英语',
      chinese: '简体中文',
      submit: '创建账户',
      submitting: '创建账户中...',
      successTitle: '注册成功！',
      successMessage: '请检查您的电子邮件以在登录前验证您的账户。',
      goToLogin: '前往登录',
      alreadyHaveAccount: '已有账户？',
      signIn: '登录',
      passwordStrength: '密码强度：',
      weak: '弱',
      medium: '中等',
      strong: '强',
      veryStrong: '非常强',
      optional: '可选'
    }
  };

  const t = content?.[language];

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password?.length >= 8) strength++;
    if (password?.length >= 12) strength++;
    if (/[a-z]/?.test(password) && /[A-Z]/?.test(password)) strength++;
    if (/\d/?.test(password)) strength++;
    if (/[^a-zA-Z0-9]/?.test(password)) strength++;
    return Math.min(strength, 4);
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
    
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\+?[0-9]{10,15}$/;

    if (!formData?.fullName?.trim()) {
      newErrors.fullName = language === 'en' ? 'Full name is required' : '姓名为必填项';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = language === 'en' ? 'Email is required' : '电子邮件为必填项';
    } else if (!emailRegex?.test(formData?.email)) {
      newErrors.email = language === 'en' ? 'Invalid email format' : '电子邮件格式无效';
    }

    if (!formData?.phone?.trim()) {
      newErrors.phone = language === 'en' ? 'Phone number is required' : '电话号码为必填项';
    } else if (!phoneRegex?.test(formData?.phone)) {
      newErrors.phone = language === 'en' ? 'Invalid phone format' : '电话号码格式无效';
    }

    if (!formData?.password) {
      newErrors.password = language === 'en' ? 'Password is required' : '密码为必填项';
    } else if (formData?.password?.length < 8) {
      newErrors.password = language === 'en' ? 'Password must be at least 8 characters' : '密码至少需要8个字符';
    }

    if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = language === 'en' ? 'Passwords do not match' : '密码不匹配';
    }

    if (formData?.accountType === 'company' && !formData?.companyName?.trim()) {
      newErrors.companyName = language === 'en' ? 'Company name is required' : '公司名称为必填项';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      await buyerAuthService?.signUp(formData);
      setRegistrationSuccess(true);
    } catch (error) {
      setErrors({ submit: error?.message || (language === 'en' ? 'Registration failed. Please try again.' : '注册失败。请重试。') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPasswordStrengthColor = () => {
    const colors = ['#ef4444', '#f59e0b', '#eab308', '#22c55e'];
    return colors?.[passwordStrength - 1] || '#e5e7eb';
  };

  const getPasswordStrengthLabel = () => {
    const labels = [t?.weak, t?.medium, t?.strong, t?.veryStrong];
    return labels?.[passwordStrength - 1] || '';
  };

  if (registrationSuccess) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-[60px]">
          <FullWidthSection py="py-16">
            <ContentWrapper maxWidth="md">
            <div className="bg-card rounded-lg shadow-card border border-border p-8 text-center">
              <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircle" size={32} color="var(--color-success)" />
              </div>
              <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
                {t?.successTitle}
              </h2>
              <p className="text-muted-foreground mb-6">
                {t?.successMessage}
              </p>
              <Button
                onClick={() => navigate('/buyer-login')}
                className="w-full"
                iconName="ArrowRight"
                iconPosition="right"
              >
                {t?.goToLogin}
              </Button>
            </div>
            </ContentWrapper>
          </FullWidthSection>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
        <main className="pt-[60px]">
          <FullWidthSection padding="" py="py-8">
            <ContentWrapper maxWidth="4xl" padding="px-5">
          <div className="bg-card rounded-lg shadow-card border border-border p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="UserPlus" size={32} color="var(--color-primary)" />
              </div>
              <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                {t?.title}
              </h1>
              <p className="text-muted-foreground">{t?.subtitle}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  {t?.accountType}
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, accountType: 'individual' }))}
                    className={`p-4 rounded-md border-2 transition-smooth text-left ${
                      formData?.accountType === 'individual' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }`}
                  >
                    <Icon name="User" size={20} className="mb-2" />
                    <div className="font-medium">{t?.individual}</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, accountType: 'company' }))}
                    className={`p-4 rounded-md border-2 transition-smooth text-left ${
                      formData?.accountType === 'company' ?'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                    }`}
                  >
                    <Icon name="Building2" size={20} className="mb-2" />
                    <div className="font-medium">{t?.company}</div>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label={t?.fullName}
                  name="fullName"
                  value={formData?.fullName}
                  onChange={handleChange}
                  error={errors?.fullName}
                  required
                />
                <Input
                  label={t?.email}
                  name="email"
                  type="email"
                  value={formData?.email}
                  onChange={handleChange}
                  error={errors?.email}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label={t?.phone}
                  name="phone"
                  value={formData?.phone}
                  onChange={handleChange}
                  error={errors?.phone}
                  required
                />
                {formData?.accountType === 'company' && (
                  <Input
                    label={t?.companyName}
                    name="companyName"
                    value={formData?.companyName}
                    onChange={handleChange}
                    error={errors?.companyName}
                    required
                  />
                )}
              </div>

              <div>
                <Input
                  label={t?.password}
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData?.password}
                  onChange={handleChange}
                  error={errors?.password}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-sm text-primary hover:underline mt-1"
                >
                  {showPassword ? (language === 'en' ? 'Hide' : '隐藏') : (language === 'en' ? 'Show' : '显示')}
                </button>
                {formData?.password && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm text-muted-foreground">{t?.passwordStrength}</span>
                      <span className="text-sm font-medium" style={{ color: getPasswordStrengthColor() }}>
                        {getPasswordStrengthLabel()}
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full transition-all"
                        style={{
                          width: `${(passwordStrength / 4) * 100}%`,
                          backgroundColor: getPasswordStrengthColor()
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>

              <Input
                label={t?.confirmPassword}
                name="confirmPassword"
                type="password"
                value={formData?.confirmPassword}
                onChange={handleChange}
                error={errors?.confirmPassword}
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label={`${t?.businessLicense} (${t?.optional})`}
                  name="businessLicenseNumber"
                  value={formData?.businessLicenseNumber}
                  onChange={handleChange}
                />
                <Input
                  label={`${t?.importLicense} (${t?.optional})`}
                  name="importLicenseNumber"
                  value={formData?.importLicenseNumber}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  {t?.preferredLanguage}
                </label>
                <select
                  name="preferredLanguage"
                  value={formData?.preferredLanguage}
                  onChange={handleChange}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="en">{t?.english}</option>
                  <option value="zh">{t?.chinese}</option>
                </select>
              </div>

              {errors?.submit && (
                <div className="p-3 bg-destructive/10 border border-destructive rounded-md">
                  <p className="text-sm text-destructive">{errors?.submit}</p>
                </div>
              )}

              <Button
                type="submit"
                loading={isSubmitting}
                className="w-full"
                iconName="UserPlus"
              >
                {isSubmitting ? t?.submitting : t?.submit}
              </Button>

              <div className="text-center text-sm text-muted-foreground">
                {t?.alreadyHaveAccount}{' '}
                <Link to="/buyer-login" className="text-primary hover:underline font-medium">
                  {t?.signIn}
                </Link>
              </div>
            </form>
          </div>
          </ContentWrapper>
        </FullWidthSection>
      </main>
    </div>
  );
};

export default BuyerRegistration;